import { ErrorMessage } from '@hookform/error-message';
import {
  Box,
  FormControl,
  InputAdornment,
  InputLabel,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import { format } from 'date-fns';
import { FC, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { DATE_FORMAT_API } from '../../../../../../../../@constants/date';
import { Error } from '../../../../../../../../components/Common/Error';
import DateRangePicker from '../../../../../../../../components/DateRangePicker';
import { MenuItem } from '../../../../../../../../components/styled/SelectMenuItem';
import { Calendar2Icon } from '../../../../../../../../components/SvgIcons/Calendar2Icon';
import { FileExcelIcon } from '../../../../../../../../components/SvgIcons/FileExcelIcon';
import { FilePdfIcon } from '../../../../../../../../components/SvgIcons/FilePdfIcon';
import { FooterExtraButtons } from '../../../../../../components/FooterButtons';
import { CompanyField } from '../../../../../../components/Inputs/CompanyField';
import { ReportLabel } from '../../../../../../components/ReportLabel';
import {
  AccountsReceivableSalesJournalReportFormModel,
  CurrencyApplicationEnum,
  OrderByEnum,
  OrderDirectionEnum,
} from '../../models';
import { CurrencyApplicationField } from './fields/currencyApplicationField';
import { CurrencyField } from './fields/CurrencyField';
import { FixedAtField } from './fields/FixedAtField';
import { InvoiceTypeField } from './fields/InvoiceTypeField';

export const FilterForm: FC<{
  onSubmit: (data: AccountsReceivableSalesJournalReportFormModel) => void;
  onReset: () => void;
}> = ({ onSubmit, onReset }) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useFormContext<AccountsReceivableSalesJournalReportFormModel>();

  const formValues = watch();

  const dates = useMemo(
    (): (Date | null)[] => [
      formValues?.from ? new Date(formValues?.from) : null,
      formValues?.to ? new Date(formValues?.to) : null,
    ],
    [formValues?.from, formValues?.to]
  );

  const handleChangeDates = (value: (Date | null)[]) => {
    const [from, to] = value;
    const _from = from ? format(from, DATE_FORMAT_API) : null;
    const _to = to ? format(to, DATE_FORMAT_API) : null;
    setValue('from', _from);
    setValue('to', _to);
  };

  return (
    <Box sx={{ p: 0, height: '100%', backgroundColor: 'colors.gray.o10', overflowY: 'scroll' }}>
      <Stack direction={'column'} spacing={0} sx={{ width: '100%', height: '100%' }}>
        <ReportLabel label="Accounts Receivable Sales Journal" />
        <Stack direction={'row'} spacing={0}>
          <Stack
            direction={'column'}
            spacing={2}
            sx={{
              p: 3,
            }}
          >
            <DateRangePicker
              value={dates}
              onChange={handleChangeDates}
              renderInput={({ startProps, endProps }) => (
                <Stack
                  direction="row"
                  spacing="1rem"
                  sx={{
                    width: '100%',
                  }}
                >
                  <TextField
                    {...startProps}
                    size="medium"
                    variant="standard"
                    sx={{
                      width: '50%',
                    }}
                    label="From"
                    error={Boolean(errors?.from)}
                    helperText={errors?.from?.message}
                    placeholder="mm/dd/yyyy"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Calendar2Icon
                            sx={{
                              fontSize: '1rem',
                              color: ({ palette }) => palette.common.black,
                            }}
                          />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <TextField
                    {...endProps}
                    size="medium"
                    variant="standard"
                    sx={{
                      width: '50%',
                    }}
                    label="To"
                    error={Boolean(errors?.to)}
                    helperText={errors?.to?.message}
                    placeholder="mm/dd/yyyy"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Calendar2Icon
                            sx={{
                              fontSize: '1rem',
                              color: ({ palette }) => palette.common.black,
                            }}
                          />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Stack>
              )}
            />
            <Controller
              control={control}
              name="crs_number"
              render={({ field }) => (
                <TextField
                  value={field.value ?? ''}
                  onChange={e => field.onChange(e.target.value)}
                  variant="standard"
                  fullWidth
                  label={'CRS Number'}
                />
              )}
            />

            <Controller
              control={control}
              name="invoice_types"
              render={({ field, formState }) => (
                <InvoiceTypeField
                  value={field.value}
                  onChange={value => field.onChange(value)}
                  error={
                    formState?.errors[field.name] ? (
                      <ErrorMessage
                        errors={formState.errors}
                        name={field.name}
                        render={({ message }) => <Error>{message}</Error>}
                      />
                    ) : null
                  }
                />
              )}
            />
            <Controller
              control={control}
              name="card_ids"
              render={({ field }) => (
                <CompanyField value={field.value} onChange={value => field.onChange(value)} />
              )}
            />

            <Controller
              control={control}
              name="order_by"
              render={({ field }) => (
                <FormControl variant="standard" fullWidth>
                  <InputLabel>Order by</InputLabel>
                  <Select
                    value={field.value || ''}
                    onChange={e => field.onChange(e.target.value)}
                    label="Order by"
                  >
                    <MenuItem value={OrderByEnum.INVOICE_DATE}>Invoice date</MenuItem>
                    <MenuItem value={OrderByEnum.INVOICE_NUMBER}>Invoice number</MenuItem>
                    <MenuItem value={OrderByEnum.AMOUNT}>Amount</MenuItem>
                  </Select>
                </FormControl>
              )}
            />

            <Controller
              control={control}
              name="sens"
              render={({ field }) => (
                <FormControl variant="standard" fullWidth>
                  <InputLabel>Send</InputLabel>
                  <Select
                    value={field.value || ''}
                    onChange={e => field.onChange(e.target.value)}
                    label="Send"
                  >
                    <MenuItem value={OrderDirectionEnum.DESC}>Desc</MenuItem>
                    <MenuItem value={OrderDirectionEnum.ASC}>Asc</MenuItem>
                  </Select>
                </FormControl>
              )}
            />

            <Box sx={{ height: 10 }} />

            <Controller
              control={control}
              name="currency_application"
              render={({ field, formState }) => (
                <CurrencyApplicationField
                  value={field.value}
                  onChange={value => {
                    field.onChange(value);
                    if (value !== CurrencyApplicationEnum.FIXED) {
                      setValue('fixed_at', null);
                      setValue('currency', null);
                    }
                  }}
                  error={
                    formState?.errors[field.name] ? (
                      <ErrorMessage
                        errors={formState.errors}
                        name={field.name}
                        render={({ message }) => <Error>{message}</Error>}
                      />
                    ) : null
                  }
                />
              )}
            />

            {formValues.currency_application === CurrencyApplicationEnum.FIXED && (
              <>
                <Controller
                  control={control}
                  name="currency"
                  render={({ field, formState }) => (
                    <CurrencyField
                      value={field.value}
                      onChange={value => {
                        field.onChange(value);
                        if (value?.is_local) {
                          setValue('fixed_at', null);
                        }
                      }}
                      error={
                        formState?.errors[field.name] ? (
                          <ErrorMessage
                            errors={formState.errors}
                            name={field.name}
                            render={({ message }) => <Error>{message}</Error>}
                          />
                        ) : null
                      }
                    />
                  )}
                />

                <Controller
                  control={control}
                  name="fixed_at"
                  render={({ field, formState }) => (
                    <FixedAtField
                      value={field.value}
                      onChange={value => field.onChange(value)}
                      helperText={
                        <>
                          <ErrorMessage
                            errors={formState.errors}
                            name={field.name}
                            render={({ message }) => <Error>{message}</Error>}
                          />
                        </>
                      }
                    />
                  )}
                />
              </>
            )}
            <Box sx={{ height: 10 }} />
          </Stack>
        </Stack>
        {/* <FooterButtons onSubmit={handleSubmit(onSubmit)} onReset={onReset} /> */}

        <FooterExtraButtons
          onSubmit={file_type => {
            setValue('file_type', file_type);
            handleSubmit(onSubmit)();
          }}
          onReset={onReset}
          exportFileTypes={[
            {
              label: 'PDF',
              value: 'pdf',
              icon: <FilePdfIcon sx={{ width: '3rem', height: '3rem' }} />,
            },
            {
              label: 'EXCEL',
              value: 'excel',
              icon: <FileExcelIcon sx={{ width: '3rem', height: '3rem' }} />,
            },
          ]}
        />
      </Stack>
    </Box>
  );
};
